/**
 * User Progress Hooks
 */
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { userProgressService } from '../services/userProgressService';
import type {
    GetProgressPhotosResponse,
    GetWeightHistoryResponse,
    GetWaterHistoryResponse,
    GetProgressOverviewResponse,
} from '../types/userProgress.types';

export const USER_PROGRESS_QUERY_KEYS = {
    all: ['userProgress'] as const,
    photos: (userId: string) => [...USER_PROGRESS_QUERY_KEYS.all, 'photos', userId] as const,
    weight: (userId: string) => [...USER_PROGRESS_QUERY_KEYS.all, 'weight', userId] as const,
    water: (userId: string) => [...USER_PROGRESS_QUERY_KEYS.all, 'water', userId] as const,
    overview: (userId: string) => [...USER_PROGRESS_QUERY_KEYS.all, 'overview', userId] as const,
};

/**
 * Hook to get progress photos
 */
export const useGetProgressPhotos = (
    userId: string,
    options?: Omit<UseQueryOptions<GetProgressPhotosResponse, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery<GetProgressPhotosResponse, Error>({
        queryKey: USER_PROGRESS_QUERY_KEYS.photos(userId),
        queryFn: () => userProgressService.getProgressPhotos(userId),
        enabled: !!userId,
        ...options,
    });
};

/**
 * Hook to get weight history
 */
export const useGetWeightHistory = (
    userId: string,
    options?: Omit<UseQueryOptions<GetWeightHistoryResponse, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery<GetWeightHistoryResponse, Error>({
        queryKey: USER_PROGRESS_QUERY_KEYS.weight(userId),
        queryFn: () => userProgressService.getWeightHistory(userId),
        enabled: !!userId,
        ...options,
    });
};

/**
 * Hook to get water history
 */
export const useGetWaterHistory = (
    userId: string,
    options?: Omit<UseQueryOptions<GetWaterHistoryResponse, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery<GetWaterHistoryResponse, Error>({
        queryKey: USER_PROGRESS_QUERY_KEYS.water(userId),
        queryFn: () => userProgressService.getWaterHistory(userId),
        enabled: !!userId,
        ...options,
    });
};

/**
 * Hook to get progress overview
 */
export const useGetProgressOverview = (
    userId: string,
    options?: Omit<UseQueryOptions<GetProgressOverviewResponse, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery<GetProgressOverviewResponse, Error>({
        queryKey: USER_PROGRESS_QUERY_KEYS.overview(userId),
        queryFn: () => userProgressService.getProgressOverview(userId),
        enabled: !!userId,
        ...options,
    });
};
